// CoordinatePlaneLab.jsx
import React, { useState } from 'react';
import '../styles/coordinatePlaneLab.css';

/**
 * CoordinatePlaneLab
 * Modes:
 *  - "compare-line-and-plane": toggle between Number Line and Coordinate Plane views.
 *    Red dots mark every integer position. Hover a dot to see its coordinates.
 */
export default function CoordinatePlaneLab({ mode = 'compare-line-and-plane' }) {
  switch (mode) {
    case 'compare-line-and-plane':
      return <CompareMode />;
    case 'plot-point':
      return <PlotPointMode />;
    case 'reading-a-point':
      return <ReadPointMode />;
    case 'horizontal-coordinates':
      return <HorizontalCoordinatesMode />;
    case 'vertical-coordinates':
      return <VerticalCoordinatesMode />;
    case 'measure-distance':
      return <MeasureDistanceMode />;
    case 'plot-shape':
      return <PlotShapeMode />;
    case 'plot-graph':
      return <PlotGraphMode />;
    case 'plot-graph-lines':
      return <PlotGraphLinesMode />;
    default:
      return (
        <div className="coord-lab">
          <p className="coord-lab-unsupported">Mode "{mode}" is not supported yet.</p>
        </div>
      );
  }
}

/* ── Shared constants ─────────────────────────────────────────────────── */
const MIN = 0;
const MAX = 10;
const INTEGERS = Array.from({ length: MAX - MIN + 1 }, (_, i) => i + MIN); // 0..10

/* ── Compare mode ─────────────────────────────────────────────────────── */
function CompareMode() {
  const [view, setView] = useState('number-line');
  const isNL = view === 'number-line';

  return (
    <div className="coord-lab--compare">
      <div className="coord-lab-toggle">
        <button
          type="button"
          className={`coord-toggle-btn ${isNL ? 'is-active' : ''}`}
          onClick={() => setView('number-line')}
        >
          Number Line
        </button>
        <button
          type="button"
          className={`coord-toggle-btn ${!isNL ? 'is-active' : ''}`}
          onClick={() => setView('coordinate-plane')}
        >
          Coordinate Plane
        </button>
      </div>

      <div className="coord-lab-view">
        {isNL
          ? <NumberLineView />
          : <CoordinatePlaneView />
        }
      </div>

      <p className="coord-lab-caption">
        {isNL
          ? <><span className="coord-point-dot" /><strong>{INTEGERS.length} points</strong> — one for each number on the line</>
          : <><span className="coord-point-dot" /><strong>{INTEGERS.length * INTEGERS.length} points</strong> — every pair (x, y) from 0–10</>
        }
      </p>
    </div>
  );
}

/* ── Number Line SVG ──────────────────────────────────────────────────── */
function NumberLineView() {
  const [hovered, setHovered] = useState(null); // number | null

  const W = 600;        // coordinate system width (axis spans PAD_X … W-PAD_X)
  const VB_W = W + 70;  // viewBox wider than W to fit "x-axis" label after arrowhead
  const H = 110;
  const PAD_X = 44;
  const AXIS_Y = H / 2;
  const TICK_H = 10;
  const MAJOR_H = 18;
  const DOT_R = 6;
  const TIP_H = 24;
  const TIP_W = 36;
  const TIP_PAD = 6;

  // Expand viewBox height to 612 to match CoordinatePlaneView — both SVGs
  // then scale identically inside the fixed-height .coord-lab-view container,
  // eliminating dead space on toggle. Content (axis at AXIS_Y=55) is centered.
  const VB_H = 612;
  const VB_Y = -(VB_H / 2 - AXIS_Y); // -251: centers axis in the tall viewBox

  const x = (n) => PAD_X + ((n - MIN) / (MAX - MIN)) * (W - PAD_X * 2);

  // Right arrowhead tip x
  const arrowTipX = W - PAD_X + 12;

  return (
    <div className="coord-lab coord-lab--number-line">
      <svg
        viewBox={`0 ${VB_Y} ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid meet"
        className="coord-lab-svg"
        aria-label={`Number line from ${MIN} to ${MAX}`}
        role="img"
      >
        {/* Axis */}
        <line x1={PAD_X - 12} y1={AXIS_Y} x2={arrowTipX} y2={AXIS_Y} className="coord-axis" />

        {/* Arrow heads */}
        <polygon points={`${arrowTipX},${AXIS_Y} ${arrowTipX - 10},${AXIS_Y - 5} ${arrowTipX - 10},${AXIS_Y + 5}`} className="coord-arrow" />
        <polygon points={`${PAD_X - 12},${AXIS_Y} ${PAD_X - 2},${AXIS_Y - 5} ${PAD_X - 2},${AXIS_Y + 5}`} className="coord-arrow" />

        {/* x-axis label — inline with the axis, just after the right arrowhead */}
        <text
          x={arrowTipX + 8} y={AXIS_Y + 4}
          textAnchor="start" dominantBaseline="middle"
          className="coord-label coord-label--annotation"
        >x-axis</text>

        {/* Ticks, labels, red dots */}
        {INTEGERS.map((n) => {
          const cx = x(n);
          const isZero = n === 0;
          const tickHalf = isZero ? MAJOR_H : TICK_H;
          const isHovered = hovered === n;

          // Keep tooltip inside SVG bounds
          const tipX = Math.min(Math.max(cx - TIP_W / 2, TIP_PAD), W - TIP_W - TIP_PAD);
          const tipY = AXIS_Y - DOT_R - TIP_H - 6;

          return (
            <g key={n}>
              <line
                x1={cx} y1={AXIS_Y - tickHalf}
                x2={cx} y2={AXIS_Y + tickHalf}
                className={isZero ? 'coord-tick coord-tick--major' : 'coord-tick'}
              />
              <text
                x={cx} y={AXIS_Y + tickHalf + 15}
                textAnchor="middle"
                className={isZero ? 'coord-label coord-label--zero' : 'coord-label'}
              >
                {n}
              </text>
              <circle
                cx={cx} cy={AXIS_Y} r={isHovered ? DOT_R + 2 : DOT_R}
                className={`coord-point ${isHovered ? 'coord-point--hovered' : ''}`}
                onMouseEnter={() => setHovered(n)}
                onMouseLeave={() => setHovered(null)}
                style={{ cursor: 'pointer' }}
              />
              {isHovered && (
                <g className="coord-tooltip">
                  <rect x={tipX} y={tipY} width={TIP_W} height={TIP_H} rx={4} className="coord-tooltip-bg" />
                  <text x={tipX + TIP_W / 2} y={tipY + TIP_H / 2 + 4} textAnchor="middle" className="coord-tooltip-text">
                    {n}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ── Coordinate Plane SVG ─────────────────────────────────────────────── */
function CoordinatePlaneView() {
  const [hovered, setHovered] = useState(null); // [nx, ny] | null

  // Same constants as PlotCanvas / ReadCanvas so grid cells are identical
  const CELL = 46;
  const PAD = 54;
  const PLOT_W = MAX * CELL;      // 460
  const PLOT_H = MAX * CELL;      // 460
  const W = PAD + PLOT_W + PAD;   // 568
  const H = PAD + PLOT_H + PAD;   // 568
  const VB_RIGHT = 72;
  const VB_TOP = 44;
  const DOT_R = 4;
  const TIP_W = 52;
  const TIP_H = 24;
  const TIP_PAD = 4;

  const ox = PAD;
  const oy = H - PAD;

  const px = (n) => ox + (n / MAX) * PLOT_W;
  const py = (n) => oy - (n / MAX) * PLOT_H;

  const TICK = 5;
  const LABEL_OFFSET = 14;

  // Arrowhead tips
  const xArrowTip = ox + PLOT_W + 14;
  const yArrowTip = oy - PLOT_H - 14;

  return (
    <div className="coord-lab coord-lab--plane">
      <svg
        viewBox={`0 ${-VB_TOP} ${W + VB_RIGHT} ${H + VB_TOP}`}
        preserveAspectRatio="xMidYMid meet"
        className="coord-lab-svg"
        aria-label="Coordinate plane from 0 to 10 on both axes"
        role="img"
      >
        {/* Grid lines — rendered first so axes and dots sit on top */}
        {INTEGERS.filter(n => n > 0).map((n) => (
          <g key={n}>
            {/* Vertical grid line at x=n */}
            <line x1={px(n)} y1={oy} x2={px(n)} y2={oy - PLOT_H} className="coord-grid" />
            {/* Horizontal grid line at y=n */}
            <line x1={ox} y1={py(n)} x2={ox + PLOT_W} y2={py(n)} className="coord-grid" />
          </g>
        ))}

        {/* Axes */}
        <line x1={ox - 10} y1={oy} x2={xArrowTip} y2={oy} className="coord-axis" />
        <line x1={ox} y1={oy + 10} x2={ox} y2={yArrowTip} className="coord-axis" />

        {/* Arrowheads */}
        <polygon points={`${xArrowTip},${oy} ${xArrowTip - 10},${oy - 5} ${xArrowTip - 10},${oy + 5}`} className="coord-arrow" />
        <polygon points={`${ox},${yArrowTip} ${ox - 5},${yArrowTip + 10} ${ox + 5},${yArrowTip + 10}`} className="coord-arrow" />

        {/* x-axis label — inline with axis, just after the right arrowhead */}
        <text
          x={xArrowTip + 8} y={oy}
          textAnchor="start" dominantBaseline="middle"
          className="coord-label coord-label--annotation"
        >x-axis</text>

        {/* y-axis label — rotated to read upward, just above the top arrowhead, aligned with axis */}
        <text
          transform="rotate(-90)"
          x={-(yArrowTip - 8)}
          y={ox - 12}
          textAnchor="end"
          className="coord-label coord-label--annotation"
        >y-axis</text>

        {/* Ticks + number labels */}
        {INTEGERS.map((n) => {
          if (n === 0) return null;
          return (
            <g key={n}>
              <line x1={px(n)} y1={oy - TICK} x2={px(n)} y2={oy + TICK} className="coord-tick" />
              <text x={px(n)} y={oy + LABEL_OFFSET + 4} textAnchor="middle" className="coord-label">{n}</text>
              <line x1={ox - TICK} y1={py(n)} x2={ox + TICK} y2={py(n)} className="coord-tick" />
              <text x={ox - LABEL_OFFSET} y={py(n) + 4} textAnchor="end" className="coord-label">{n}</text>
            </g>
          );
        })}

        {/* Origin */}
        <text x={ox - 8} y={oy + LABEL_OFFSET + 4} textAnchor="end" className="coord-label coord-label--zero">0</text>
        <text x={ox + 6} y={oy + LABEL_OFFSET + 4} textAnchor="start" dominantBaseline="auto" className="coord-label coord-label--annotation">origin</text>

        {/* Red dots — rendered before tooltip so tooltip renders on top */}
        {INTEGERS.map((nx) =>
          INTEGERS.map((ny) => {
            const isHovered = hovered && hovered[0] === nx && hovered[1] === ny;
            return (
              <circle
                key={`${nx}-${ny}`}
                cx={px(nx)}
                cy={py(ny)}
                r={isHovered ? DOT_R + 2 : DOT_R}
                className={`coord-point ${isHovered ? 'coord-point--hovered' : ''}`}
                onMouseEnter={() => setHovered([nx, ny])}
                onMouseLeave={() => setHovered(null)}
                style={{ cursor: 'pointer' }}
              />
            );
          })
        )}

        {/* Tooltip — rendered last so it sits on top of everything */}
        {hovered && (() => {
          const [nx, ny] = hovered;
          const cx = px(nx);
          const cy = py(ny);
          const tipX = Math.min(Math.max(cx - TIP_W / 2, TIP_PAD), W + VB_RIGHT - TIP_W - TIP_PAD);
          const tipY = cy - DOT_R - TIP_H - 6;
          const label = `(${nx}, ${ny})`;
          return (
            <g className="coord-tooltip" style={{ pointerEvents: 'none' }}>
              <rect x={tipX} y={tipY} width={TIP_W} height={TIP_H} rx={4} className="coord-tooltip-bg" />
              <text x={tipX + TIP_W / 2} y={tipY + TIP_H / 2 + 4} textAnchor="middle" className="coord-tooltip-text">
                {label}
              </text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
}

/* ── Plot Point mode ──────────────────────────────────────────────────── */
// Steps:
//  0 → idle: enter x and y, press "Draw x-coordinate"
//  1 → x marked: segment on x-axis from origin to (x,0), press "Draw y-coordinate"
//  2 → y marked: segment on y-axis from origin to (0,y), press "Find intersection"
//  3 → done: dashed guides from (x,0) up and (0,y) right meet at point (x,y)

function PlotPointMode() {
  const [inputX, setInputX] = useState('');
  const [inputY, setInputY] = useState('');
  const [step, setStep] = useState(0);
  const [point, setPoint] = useState(null);

  const parsedX = parseInt(inputX, 10);
  const parsedY = parseInt(inputY, 10);
  const valid =
    !Number.isNaN(parsedX) && parsedX >= 1 && parsedX <= 9 &&
    !Number.isNaN(parsedY) && parsedY >= 1 && parsedY <= 9;

  const handleDice = () => {
    setInputX(String(Math.floor(Math.random() * 9) + 1));
    setInputY(String(Math.floor(Math.random() * 9) + 1));
  };
  const handleDrawX = () => { if (!valid) return; setPoint({ x: parsedX, y: parsedY }); setStep(1); };
  const handleDrawY = () => setStep(2);
  const handleFindIntersection = () => setStep(3);
  const handleReset = () => { setStep(0); setPoint(null); setInputX(''); setInputY(''); };

  const STEP_DEFS = [
    { label: 'Draw x-coordinate', action: handleDrawX },
    { label: 'Draw y-coordinate', action: handleDrawY },
    { label: 'Find intersection',  action: handleFindIntersection },
  ];

  return (
    <div className="coord-lab--plot">
      <div className="coord-plot-canvas-wrap">
        <PlotCanvas point={point} step={step} />
      </div>

      <div className="coord-plot-controls">
        {/* Coordinate inputs + dice */}
        <div className="coord-plot-input-group">
          <div className="coord-plot-input-label">Point</div>
          <div className="coord-plot-fields">
            <div className="coord-plot-field">
              <span className="coord-plot-field-label coord-plot-field-label--x">x</span>
              <input
                className="coord-plot-input"
                type="number" min="1" max="9"
                value={inputX}
                onChange={(e) => setInputX(e.target.value)}
                disabled={step > 0}
                placeholder="1–9"
              />
            </div>
            <div className="coord-plot-field">
              <span className="coord-plot-field-label coord-plot-field-label--y">y</span>
              <input
                className="coord-plot-input"
                type="number" min="1" max="9"
                value={inputY}
                onChange={(e) => setInputY(e.target.value)}
                disabled={step > 0}
                placeholder="1–9"
              />
            </div>
          </div>
          <button
            type="button"
            className="coord-plot-dice-btn"
            onClick={handleDice}
            disabled={step > 0}
            title="Pick random coordinates"
          >
            🎲 Random
          </button>
        </div>

        {/* Divider */}
        <div className="coord-plot-divider" />

        {/* Step list */}
        <div className="coord-plot-steps">
          {STEP_DEFS.map((s, i) => {
            const done = step > i;
            const active = step === i;
            return (
              <button
                key={i}
                type="button"
                className={`coord-plot-step-btn ${done ? 'is-done' : ''} ${active ? 'is-active' : ''}`}
                onClick={s.action}
                disabled={step !== i || (i === 0 && !valid)}
              >
                <span className="coord-plot-step-num">{done ? '✓' : i + 1}</span>
                <span className="coord-plot-step-label">{s.label}</span>
              </button>
            );
          })}
        </div>

        {/* Reset */}
        {step === 3 && (
          <button type="button" className="btn btn-secondary coord-plot-reset-btn" onClick={handleReset}>
            Try another
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Plot Canvas SVG ──────────────────────────────────────────────────── */
function PlotCanvas({ point, step }) {
  const CELL = 46;
  const PAD = 54;
  const PLOT_W = MAX * CELL;
  const PLOT_H = MAX * CELL;
  const W = PAD + PLOT_W + PAD;
  const H = PAD + PLOT_H + PAD;
  const VB_RIGHT = 72;
  const VB_TOP = 44;

  const ox = PAD;
  const oy = H - PAD;

  const svgX = (n) => ox + (n / MAX) * PLOT_W;
  const svgY = (n) => oy - (n / MAX) * PLOT_H;

  const TICK = 5;
  const LABEL_OFFSET = 14;
  const xArrowTip = ox + PLOT_W + 14;
  const yArrowTip = oy - PLOT_H - 14;

  const pt = point ? { x: svgX(point.x), y: svgY(point.y) } : null;

  // Keep "(x, y)" label inside the viewBox
  const labelAnchor  = point && point.x >= 7 ? 'end'  : 'start';
  const labelOffsetX = point && point.x >= 7 ? -10    : 10;
  const labelOffsetY = point && point.y >= 9 ?  18    : -10;

  return (
    <svg
      viewBox={`0 ${-VB_TOP} ${W + VB_RIGHT} ${H + VB_TOP}`}
      className="coord-lab-svg--natural"
      aria-label="Coordinate plane for plotting a point"
      role="img"
    >
        {/* Grid */}
        {INTEGERS.filter((n) => n > 0).map((n) => (
          <g key={n}>
            <line x1={svgX(n)} y1={oy} x2={svgX(n)} y2={oy - PLOT_H} className="coord-grid" />
            <line x1={ox} y1={svgY(n)} x2={ox + PLOT_W} y2={svgY(n)} className="coord-grid" />
          </g>
        ))}

        {/* Axes */}
        <line x1={ox - 10} y1={oy} x2={xArrowTip} y2={oy} className="coord-axis" />
        <line x1={ox} y1={oy + 10} x2={ox} y2={yArrowTip} className="coord-axis" />

        {/* Arrowheads */}
        <polygon points={`${xArrowTip},${oy} ${xArrowTip-10},${oy-5} ${xArrowTip-10},${oy+5}`} className="coord-arrow" />
        <polygon points={`${ox},${yArrowTip} ${ox-5},${yArrowTip+10} ${ox+5},${yArrowTip+10}`} className="coord-arrow" />

        {/* Axis labels */}
        <text x={xArrowTip+8} y={oy} textAnchor="start" dominantBaseline="middle" className="coord-label coord-label--annotation">x-axis</text>
        <text transform="rotate(-90)" x={-(yArrowTip-8)} y={ox-12} textAnchor="end" className="coord-label coord-label--annotation">y-axis</text>

        {/* Ticks + number labels */}
        {INTEGERS.map((n) => {
          if (n === 0) return null;
          const hlX = step >= 1 && point && point.x === n;
          const hlY = step >= 2 && point && point.y === n;
          return (
            <g key={n}>
              <line x1={svgX(n)} y1={oy-TICK} x2={svgX(n)} y2={oy+TICK} className={hlX ? 'coord-tick coord-tick--active' : 'coord-tick'} />
              <text x={svgX(n)} y={oy+LABEL_OFFSET+4} textAnchor="middle" className={hlX ? 'coord-label coord-label--active' : 'coord-label'}>{n}</text>
              <line x1={ox-TICK} y1={svgY(n)} x2={ox+TICK} y2={svgY(n)} className={hlY ? 'coord-tick coord-tick--active' : 'coord-tick'} />
              <text x={ox-LABEL_OFFSET} y={svgY(n)+4} textAnchor="end" className={hlY ? 'coord-label coord-label--active' : 'coord-label'}>{n}</text>
            </g>
          );
        })}

        {/* Origin */}
        <text x={ox-8} y={oy+LABEL_OFFSET+4} textAnchor="end" className="coord-label coord-label--zero">0</text>
        <text x={ox+6} y={oy+LABEL_OFFSET+4} textAnchor="start" className="coord-label coord-label--annotation">origin</text>

        {/* Step 1 — animated segment along x-axis from origin to (x, 0) */}
        {pt && step >= 1 && (() => {
          const xLen = Math.round(pt.x - ox);
          return (
            <>
              <line
                key={`x-seg-${point.x}-${point.y}`}
                x1={ox} y1={oy} x2={pt.x} y2={oy}
                className="coord-guide coord-guide--x coord-guide--axis"
                style={{ '--line-len': xLen }}
              />
              <circle cx={pt.x} cy={oy} r={4} className="coord-intercept coord-intercept--x"
                style={{ animation: `pointPop 0.2s ease 1.3s both` }} />
              <text x={pt.x} y={oy+LABEL_OFFSET+4} textAnchor="middle"
                className="coord-label coord-label--active"
                style={{ animation: `labelFadeIn 0.2s ease 1.35s both` }}>{point.x}</text>
            </>
          );
        })()}

        {/* Step 2 — animated segment along y-axis from origin to (0, y) */}
        {pt && step >= 2 && (() => {
          const yLen = Math.round(oy - pt.y);
          return (
            <>
              <line
                key={`y-seg-${point.x}-${point.y}`}
                x1={ox} y1={oy} x2={ox} y2={pt.y}
                className="coord-guide coord-guide--y coord-guide--axis"
                style={{ '--line-len': yLen }}
              />
              <circle cx={ox} cy={pt.y} r={4} className="coord-intercept coord-intercept--y"
                style={{ animation: `pointPop 0.2s ease 1.3s both` }} />
              <text x={ox-LABEL_OFFSET} y={pt.y+4} textAnchor="end"
                className="coord-label coord-label--active"
                style={{ animation: `labelFadeIn 0.2s ease 1.35s both` }}>{point.y}</text>
            </>
          );
        })()}

        {/* Step 3 — dashed guides from each intercept to the intersection */}
        {pt && step >= 3 && (() => {
          return (
            <>
              <line
                key={`v-guide-${point.x}-${point.y}`}
                x1={pt.x} y1={oy} x2={pt.x} y2={pt.y}
                className="coord-guide coord-guide--x coord-guide--dashed"
              />
              <line
                key={`h-guide-${point.x}-${point.y}`}
                x1={ox} y1={pt.y} x2={pt.x} y2={pt.y}
                className="coord-guide coord-guide--y coord-guide--dashed"
                style={{ animationDelay: '0.4s' }}
              />
              <g key={`pt-${point.x}-${point.y}`}>
                <circle cx={pt.x} cy={pt.y} r={6} className="coord-plotted-point"
                  style={{ animationDelay: '0.75s' }} />
                <text
                  x={pt.x+labelOffsetX} y={pt.y+labelOffsetY}
                  textAnchor={labelAnchor}
                  className="coord-label coord-label--point-label"
                  style={{ animation: 'labelFadeIn 0.2s ease 0.85s both' }}
                >({point.x}, {point.y})</text>
              </g>
            </>
          );
        })()}
    </svg>
  );
}

/* ── Read Point mode ──────────────────────────────────────────────────── */

// Steps:
//  0 → idle: enter x and y, press "Show point"
//  1 → point visible: press "Find x-coordinate"
//  2 → vertical line drops from point to x-axis: press "Find y-coordinate"
//  3 → horizontal line extends from point to y-axis; ordered pair revealed
function ReadPointMode() {
  const [inputX, setInputX] = useState('');
  const [inputY, setInputY] = useState('');
  const [step, setStep]     = useState(0);
  const [point, setPoint]   = useState(null);

  const parsedX = parseInt(inputX, 10);
  const parsedY = parseInt(inputY, 10);
  const valid =
    !Number.isNaN(parsedX) && parsedX >= 1 && parsedX <= 9 &&
    !Number.isNaN(parsedY) && parsedY >= 1 && parsedY <= 9;

  const handleDice  = () => {
    setInputX(String(Math.floor(Math.random() * 9) + 1));
    setInputY(String(Math.floor(Math.random() * 9) + 1));
  };
  const handleShow  = () => { if (!valid) return; setPoint({ x: parsedX, y: parsedY }); setStep(1); };
  const handleFindX = () => setStep(2);
  const handleFindY = () => setStep(3);
  const handleReset = () => { setStep(0); setPoint(null); setInputX(''); setInputY(''); };

  const STEP_DEFS = [
    { label: 'Show point',        action: handleShow,  disabledOverride: !valid },
    { label: 'Find x-coordinate', action: handleFindX, disabledOverride: false },
    { label: 'Find y-coordinate', action: handleFindY, disabledOverride: false },
  ];

  return (
    <div className="coord-lab--plot">
      <div className="coord-plot-canvas-wrap">
        <ReadCanvas point={point} step={step} />
      </div>

      <div className="coord-plot-controls">
        <div className="coord-plot-input-group">
          <div className="coord-plot-input-label">Point</div>
          <div className="coord-plot-fields">
            <div className="coord-plot-field">
              <span className="coord-plot-field-label coord-plot-field-label--x">x</span>
              <input
                className="coord-plot-input"
                type="number" min="1" max="9"
                value={inputX}
                onChange={(e) => setInputX(e.target.value)}
                disabled={step > 0}
                placeholder="1–9"
              />
            </div>
            <div className="coord-plot-field">
              <span className="coord-plot-field-label coord-plot-field-label--y">y</span>
              <input
                className="coord-plot-input"
                type="number" min="1" max="9"
                value={inputY}
                onChange={(e) => setInputY(e.target.value)}
                disabled={step > 0}
                placeholder="1–9"
              />
            </div>
          </div>
          <button
            type="button"
            className="coord-plot-dice-btn"
            onClick={handleDice}
            disabled={step > 0}
            title="Pick random coordinates"
          >
            🎲 Random
          </button>
        </div>

        <div className="coord-plot-divider" />

        <div className="coord-plot-steps">
          {STEP_DEFS.map((s, i) => {
            const done   = step > i;
            const active = step === i;
            return (
              <button
                key={i}
                type="button"
                className={`coord-plot-step-btn ${done ? 'is-done' : ''} ${active ? 'is-active' : ''}`}
                onClick={s.action}
                disabled={step !== i || s.disabledOverride}
              >
                <span className="coord-plot-step-num">{done ? '✓' : i + 1}</span>
                <span className="coord-plot-step-label">{s.label}</span>
              </button>
            );
          })}
        </div>

        {step === 3 && (
          <button type="button" className="btn btn-secondary coord-plot-reset-btn" onClick={handleReset}>
            Try another
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Read Canvas SVG ──────────────────────────────────────────────────── */
function ReadCanvas({ point, step }) {
  const CELL     = 46;
  const PAD      = 54;
  const PLOT_W   = MAX * CELL;
  const PLOT_H   = MAX * CELL;
  const W        = PAD + PLOT_W + PAD;
  const H        = PAD + PLOT_H + PAD;
  const VB_RIGHT = 72;
  const VB_TOP   = 44;

  const ox = PAD;
  const oy = H - PAD;

  const svgX = (n) => ox + (n / MAX) * PLOT_W;
  const svgY = (n) => oy - (n / MAX) * PLOT_H;

  const TICK         = 5;
  const LABEL_OFFSET = 14;
  const xArrowTip    = ox + PLOT_W + 14;
  const yArrowTip    = oy - PLOT_H - 14;

  const pt = point ? { x: svgX(point.x), y: svgY(point.y) } : null;

  const labelAnchor  = point && point.x >= 7 ? 'end'  : 'start';
  const labelOffsetX = point && point.x >= 7 ? -10    : 10;
  const labelOffsetY = point && point.y >= 9 ?  18    : -12;

  return (
    <svg
      viewBox={`0 ${-VB_TOP} ${W + VB_RIGHT} ${H + VB_TOP}`}
      className="coord-lab-svg--natural"
      aria-label="Coordinate plane for reading a point"
      role="img"
    >
        {/* Grid */}
        {INTEGERS.filter((n) => n > 0).map((n) => (
          <g key={n}>
            <line x1={svgX(n)} y1={oy} x2={svgX(n)} y2={oy - PLOT_H} className="coord-grid" />
            <line x1={ox} y1={svgY(n)} x2={ox + PLOT_W} y2={svgY(n)} className="coord-grid" />
          </g>
        ))}

        {/* Axes */}
        <line x1={ox - 10} y1={oy} x2={xArrowTip} y2={oy} className="coord-axis" />
        <line x1={ox} y1={oy + 10} x2={ox} y2={yArrowTip} className="coord-axis" />

        {/* Arrowheads */}
        <polygon points={`${xArrowTip},${oy} ${xArrowTip-10},${oy-5} ${xArrowTip-10},${oy+5}`} className="coord-arrow" />
        <polygon points={`${ox},${yArrowTip} ${ox-5},${yArrowTip+10} ${ox+5},${yArrowTip+10}`} className="coord-arrow" />

        {/* Axis labels */}
        <text x={xArrowTip+8} y={oy} textAnchor="start" dominantBaseline="middle" className="coord-label coord-label--annotation">x-axis</text>
        <text transform="rotate(-90)" x={-(yArrowTip-8)} y={ox-12} textAnchor="end" className="coord-label coord-label--annotation">y-axis</text>

        {/* Ticks + number labels */}
        {INTEGERS.map((n) => {
          if (n === 0) return null;
          const hlX = step >= 2 && point && point.x === n;
          const hlY = step >= 3 && point && point.y === n;
          return (
            <g key={n}>
              <line x1={svgX(n)} y1={oy-TICK} x2={svgX(n)} y2={oy+TICK}
                className={hlX ? 'coord-tick coord-tick--active' : 'coord-tick'} />
              <text x={svgX(n)} y={oy+LABEL_OFFSET+4} textAnchor="middle"
                className={hlX ? 'coord-label coord-label--active' : 'coord-label'}>{n}</text>
              <line x1={ox-TICK} y1={svgY(n)} x2={ox+TICK} y2={svgY(n)}
                className={hlY ? 'coord-tick coord-tick--active' : 'coord-tick'} />
              <text x={ox-LABEL_OFFSET} y={svgY(n)+4} textAnchor="end"
                className={hlY ? 'coord-label coord-label--active' : 'coord-label'}>{n}</text>
            </g>
          );
        })}

        {/* Origin */}
        <text x={ox-8} y={oy+LABEL_OFFSET+4} textAnchor="end" className="coord-label coord-label--zero">0</text>
        <text x={ox+6} y={oy+LABEL_OFFSET+4} textAnchor="start" className="coord-label coord-label--annotation">origin</text>

        {/* Step 2 — vertical projection from point down to x-axis (reading x) */}
        {pt && step >= 2 && (() => {
          const len = Math.round(oy - pt.y);
          return (
            <>
              <line
                key={`x-read-${point.x}-${point.y}`}
                x1={pt.x} y1={pt.y} x2={pt.x} y2={oy}
                className="coord-guide coord-guide--x coord-guide--read"
                style={{ '--line-len': len }}
              />
              <circle cx={pt.x} cy={oy} r={4} className="coord-intercept coord-intercept--x"
                style={{ animation: 'pointPop 0.2s ease 1.3s both' }} />
              <text x={pt.x} y={oy+LABEL_OFFSET+4} textAnchor="middle"
                className="coord-label coord-label--active"
                style={{ animation: 'labelFadeIn 0.2s ease 1.35s both' }}>{point.x}</text>
            </>
          );
        })()}

        {/* Step 3 — horizontal projection from point left to y-axis (reading y) + ordered pair */}
        {pt && step >= 3 && (() => {
          const len = Math.round(pt.x - ox);
          return (
            <>
              <line
                key={`y-read-${point.x}-${point.y}`}
                x1={pt.x} y1={pt.y} x2={ox} y2={pt.y}
                className="coord-guide coord-guide--y coord-guide--read"
                style={{ '--line-len': len }}
              />
              <circle cx={ox} cy={pt.y} r={4} className="coord-intercept coord-intercept--y"
                style={{ animation: 'pointPop 0.2s ease 1.3s both' }} />
              <text x={ox-LABEL_OFFSET} y={pt.y+4} textAnchor="end"
                className="coord-label coord-label--active"
                style={{ animation: 'labelFadeIn 0.2s ease 1.35s both' }}>{point.y}</text>
              <text
                x={pt.x + labelOffsetX} y={pt.y + labelOffsetY}
                textAnchor={labelAnchor}
                className="coord-label coord-label--point-label"
                style={{ animation: 'labelFadeIn 0.3s ease 1.55s both' }}
              >({point.x}, {point.y})</text>
            </>
          );
        })()}

        {/* Point — rendered last so it always sits on top of projection lines */}
        {pt && step >= 1 && (
          <circle
            key={`pt-${point.x}-${point.y}`}
            cx={pt.x} cy={pt.y} r={6}
            className="coord-plotted-point"
          />
        )}
    </svg>
  );
}

/* ── Horizontal Coordinates mode ──────────────────────────────────────── */
const HORIZ_DEFAULTS = [
  { label: 'A', x: 1, y: 4 },
  { label: 'B', x: 5, y: 4 },
  { label: 'C', x: 8, y: 4 },
];

function HorizontalCoordinatesMode() {
  const toInputs = (pts) => pts.map((p) => ({ ...p, inputX: String(p.x), inputY: String(p.y) }));

  const [inputs, setInputs] = useState(toInputs(HORIZ_DEFAULTS));
  const [plotted, setPlotted] = useState(null);   // null = nothing plotted yet
  const [showLine, setShowLine] = useState(false);

  const handleChange = (i, field, val) => {
    setInputs((prev) => prev.map((p, idx) => (idx === i ? { ...p, [field]: val } : p)));
  };

  const parsed = inputs.map((p) => ({
    label: p.label,
    x: parseInt(p.inputX, 10),
    y: parseInt(p.inputY, 10),
  }));

  const allValid = parsed.every(
    (p) => !isNaN(p.x) && !isNaN(p.y) && p.x >= 0 && p.x <= 10 && p.y >= 0 && p.y <= 10
  );

  const handlePlot = () => {
    if (!allValid) return;
    setShowLine(false);
    setPlotted(parsed);
    const horizontal = parsed.every((p) => p.y === parsed[0].y);
    if (horizontal) setTimeout(() => setShowLine(true), 900);
  };

  const handleReset = () => {
    setInputs(toInputs(HORIZ_DEFAULTS));
    setPlotted(null);
    setShowLine(false);
  };

  const handleRandom = () => {
    const sharedY = Math.floor(Math.random() * 9) + 1;
    const xs = [];
    while (xs.length < 3) {
      const v = Math.floor(Math.random() * 10) + 1;
      if (!xs.includes(v)) xs.push(v);
    }
    xs.sort((a, b) => a - b);
    const pts = HORIZ_DEFAULTS.map((p, i) => ({ label: p.label, x: xs[i], y: sharedY }));
    setInputs(toInputs(pts));
    setPlotted(null);
    setShowLine(false);
  };

  const isHorizontal = plotted !== null && plotted.length >= 2 && plotted.every((p) => p.y === plotted[0].y);

  return (
    <div className="coord-lab--plot">
      <div className="coord-plot-canvas-wrap">
        <HorizCanvas points={plotted || []} showLine={showLine} />
      </div>

      <div className="coord-plot-controls">
        <div className="coord-plot-input-group">
          <div className="coord-horiz-point-row coord-horiz-col-headers">
            <span className="coord-horiz-point-heading" />
            <span className="coord-plot-field-label coord-plot-field-label--x">x</span>
            <span className="coord-plot-field-label coord-plot-field-label--y">y</span>
          </div>
          {inputs.map((p, i) => (
            <div key={p.label} className="coord-horiz-point-row">
              <span className="coord-horiz-point-heading">{p.label}</span>
              <input
                className="coord-plot-input"
                type="number" min="0" max="10"
                value={p.inputX}
                onChange={(e) => handleChange(i, 'inputX', e.target.value)}
                placeholder="0–10"
              />
              <input
                className="coord-plot-input"
                type="number" min="0" max="10"
                value={p.inputY}
                onChange={(e) => handleChange(i, 'inputY', e.target.value)}
                placeholder="0–10"
              />
            </div>
          ))}
          <button type="button" className="coord-plot-dice-btn" onClick={handleRandom}>
            🎲 Random
          </button>
        </div>

        <div className="coord-plot-divider" />

        <button
          type="button"
          className="btn btn-secondary coord-plot-reset-btn"
          onClick={handlePlot}
          disabled={!allValid}
        >
          Plot points
        </button>

        {plotted !== null && (
          <div className={`coord-horiz-feedback ${isHorizontal ? 'is-yes' : 'is-no'}`}>
            {isHorizontal
              ? `Horizontal! All y = ${plotted[0].y}`
              : `Not horizontal — y values differ`}
          </div>
        )}

        <button type="button" className="coord-plot-dice-btn" onClick={handleReset}>
          Reset
        </button>
      </div>
    </div>
  );
}

/* ── Horizontal Coordinates Canvas ────────────────────────────────────── */
function HorizCanvas({ points, showLine }) {
  const CELL   = 46;
  const PAD    = 54;
  const PLOT_W = MAX * CELL;
  const PLOT_H = MAX * CELL;
  const W      = PAD + PLOT_W + PAD;
  const H      = PAD + PLOT_H + PAD;
  const VB_RIGHT = 72;
  const VB_TOP   = 44;

  const ox = PAD;
  const oy = H - PAD;

  const svgX = (n) => ox + (n / MAX) * PLOT_W;
  const svgY = (n) => oy - (n / MAX) * PLOT_H;

  const TICK         = 5;
  const LABEL_OFFSET = 14;
  const xArrowTip    = ox + PLOT_W + 14;
  const yArrowTip    = oy - PLOT_H - 14;

  return (
    <svg
      viewBox={`0 ${-VB_TOP} ${W + VB_RIGHT} ${H + VB_TOP}`}
      className="coord-lab-svg--natural coord-lab-svg--horiz"
      aria-label="Coordinate plane with plotted points"
      role="img"
    >
      {/* Grid */}
      {INTEGERS.filter((n) => n > 0).map((n) => (
        <g key={n}>
          <line x1={svgX(n)} y1={oy} x2={svgX(n)} y2={oy - PLOT_H} className="coord-grid" />
          <line x1={ox} y1={svgY(n)} x2={ox + PLOT_W} y2={svgY(n)} className="coord-grid" />
        </g>
      ))}

      {/* Axes */}
      <line x1={ox - 10} y1={oy} x2={xArrowTip} y2={oy} className="coord-axis" />
      <line x1={ox} y1={oy + 10} x2={ox} y2={yArrowTip} className="coord-axis" />

      {/* Arrowheads */}
      <polygon points={`${xArrowTip},${oy} ${xArrowTip-10},${oy-5} ${xArrowTip-10},${oy+5}`} className="coord-arrow" />
      <polygon points={`${ox},${yArrowTip} ${ox-5},${yArrowTip+10} ${ox+5},${yArrowTip+10}`} className="coord-arrow" />

      {/* Axis labels */}
      <text x={xArrowTip+8} y={oy} textAnchor="start" dominantBaseline="middle" className="coord-label coord-label--annotation">x-axis</text>
      <text transform="rotate(-90)" x={-(yArrowTip-8)} y={ox-12} textAnchor="end" className="coord-label coord-label--annotation">y-axis</text>

      {/* Ticks + number labels — highlight the shared y row when horizontal */}
      {INTEGERS.map((n) => {
        if (n === 0) return null;
        const isSharedY = showLine && points[0] && n === points[0].y;
        return (
          <g key={n}>
            <line x1={svgX(n)} y1={oy - TICK} x2={svgX(n)} y2={oy + TICK} className="coord-tick" />
            <text x={svgX(n)} y={oy + LABEL_OFFSET + 4} textAnchor="middle" className="coord-label">{n}</text>
            <line
              x1={ox - TICK} y1={svgY(n)} x2={ox + TICK} y2={svgY(n)}
              className={isSharedY ? 'coord-tick coord-tick--active' : 'coord-tick'}
            />
            <text
              x={ox - LABEL_OFFSET} y={svgY(n) + 4} textAnchor="end"
              className={isSharedY ? 'coord-label coord-label--active' : 'coord-label'}
            >{n}</text>
          </g>
        );
      })}

      {/* Origin */}
      <text x={ox - 8} y={oy + LABEL_OFFSET + 4} textAnchor="end" className="coord-label coord-label--zero">0</text>
      <text x={ox + 6} y={oy + LABEL_OFFSET + 4} textAnchor="start" className="coord-label coord-label--annotation">origin</text>

      {/* Green horizontal guide line through all points when they share y */}
      {showLine && points.length >= 2 && (
        <line
          key={`horiz-${points[0].y}`}
          x1={ox} y1={svgY(points[0].y)}
          x2={ox + PLOT_W} y2={svgY(points[0].y)}
          className="coord-horiz-line"
        />
      )}

      {/* Points + coordinate labels */}
      {points.map((p) => {
        const cx          = svgX(p.x);
        const cy          = svgY(p.y);
        const labelAnchor  = p.x >= 8 ? 'end'  : 'start';
        const labelOffsetX = p.x >= 8 ? -10    : 10;
        const labelOffsetY = p.y >= 9 ?  18    : -10;
        return (
          <g key={`${p.label}-${p.x}-${p.y}`}>
            <circle cx={cx} cy={cy} r={6} className="coord-plotted-point" />
            <text
              x={cx + labelOffsetX} y={cy + labelOffsetY}
              textAnchor={labelAnchor}
              className="coord-label coord-label--point-label"
            >{p.label}({p.x}, {p.y})</text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── Vertical Coordinates mode ────────────────────────────────────────── */
const VERT_DEFAULTS = [
  { label: 'A', x: 4, y: 1 },
  { label: 'B', x: 4, y: 5 },
  { label: 'C', x: 4, y: 8 },
];

function VerticalCoordinatesMode() {
  const toInputs = (pts) => pts.map((p) => ({ ...p, inputX: String(p.x), inputY: String(p.y) }));

  const [inputs, setInputs]   = useState(toInputs(VERT_DEFAULTS));
  const [plotted, setPlotted] = useState(null);
  const [showLine, setShowLine] = useState(false);

  const handleChange = (i, field, val) => {
    setInputs((prev) => prev.map((p, idx) => (idx === i ? { ...p, [field]: val } : p)));
  };

  const parsed = inputs.map((p) => ({
    label: p.label,
    x: parseInt(p.inputX, 10),
    y: parseInt(p.inputY, 10),
  }));

  const allValid = parsed.every(
    (p) => !isNaN(p.x) && !isNaN(p.y) && p.x >= 0 && p.x <= 10 && p.y >= 0 && p.y <= 10
  );

  const handlePlot = () => {
    if (!allValid) return;
    setShowLine(false);
    setPlotted(parsed);
    const vertical = parsed.every((p) => p.x === parsed[0].x);
    if (vertical) setTimeout(() => setShowLine(true), 900);
  };

  const handleReset = () => {
    setInputs(toInputs(VERT_DEFAULTS));
    setPlotted(null);
    setShowLine(false);
  };

  const handleRandom = () => {
    const sharedX = Math.floor(Math.random() * 9) + 1;
    const ys = [];
    while (ys.length < 3) {
      const v = Math.floor(Math.random() * 10) + 1;
      if (!ys.includes(v)) ys.push(v);
    }
    ys.sort((a, b) => a - b);
    const pts = VERT_DEFAULTS.map((p, i) => ({ label: p.label, x: sharedX, y: ys[i] }));
    setInputs(toInputs(pts));
    setPlotted(null);
    setShowLine(false);
  };

  const isVertical = plotted !== null && plotted.length >= 2 && plotted.every((p) => p.x === plotted[0].x);

  return (
    <div className="coord-lab--plot">
      <div className="coord-plot-canvas-wrap">
        <VertCanvas points={plotted || []} showLine={showLine} />
      </div>

      <div className="coord-plot-controls">
        <div className="coord-plot-input-group">
          <div className="coord-horiz-point-row coord-horiz-col-headers">
            <span className="coord-horiz-point-heading" />
            <span className="coord-plot-field-label coord-plot-field-label--x">x</span>
            <span className="coord-plot-field-label coord-plot-field-label--y">y</span>
          </div>
          {inputs.map((p, i) => (
            <div key={p.label} className="coord-horiz-point-row">
              <span className="coord-horiz-point-heading">{p.label}</span>
              <input
                className="coord-plot-input"
                type="number" min="0" max="10"
                value={p.inputX}
                onChange={(e) => handleChange(i, 'inputX', e.target.value)}
                placeholder="0–10"
              />
              <input
                className="coord-plot-input"
                type="number" min="0" max="10"
                value={p.inputY}
                onChange={(e) => handleChange(i, 'inputY', e.target.value)}
                placeholder="0–10"
              />
            </div>
          ))}
          <button type="button" className="coord-plot-dice-btn" onClick={handleRandom}>
            🎲 Random
          </button>
        </div>

        <div className="coord-plot-divider" />

        <button
          type="button"
          className="btn btn-secondary coord-plot-reset-btn"
          onClick={handlePlot}
          disabled={!allValid}
        >
          Plot points
        </button>

        {plotted !== null && (
          <div className={`coord-horiz-feedback ${isVertical ? 'is-yes' : 'is-no'}`}>
            {isVertical
              ? `Vertical! All x = ${plotted[0].x}`
              : `Not vertical — x values differ`}
          </div>
        )}

        <button type="button" className="coord-plot-dice-btn" onClick={handleReset}>
          Reset
        </button>
      </div>
    </div>
  );
}

/* ── Vertical Coordinates Canvas ──────────────────────────────────────── */
function VertCanvas({ points, showLine }) {
  const CELL   = 46;
  const PAD    = 54;
  const PLOT_W = MAX * CELL;
  const PLOT_H = MAX * CELL;
  const W      = PAD + PLOT_W + PAD;
  const H      = PAD + PLOT_H + PAD;
  const VB_RIGHT = 72;
  const VB_TOP   = 44;

  const ox = PAD;
  const oy = H - PAD;

  const svgX = (n) => ox + (n / MAX) * PLOT_W;
  const svgY = (n) => oy - (n / MAX) * PLOT_H;

  const TICK         = 5;
  const LABEL_OFFSET = 14;
  const xArrowTip    = ox + PLOT_W + 14;
  const yArrowTip    = oy - PLOT_H - 14;

  return (
    <svg
      viewBox={`0 ${-VB_TOP} ${W + VB_RIGHT} ${H + VB_TOP}`}
      className="coord-lab-svg--natural coord-lab-svg--vert"
      aria-label="Coordinate plane with plotted points"
      role="img"
    >
      {/* Grid */}
      {INTEGERS.filter((n) => n > 0).map((n) => (
        <g key={n}>
          <line x1={svgX(n)} y1={oy} x2={svgX(n)} y2={oy - PLOT_H} className="coord-grid" />
          <line x1={ox} y1={svgY(n)} x2={ox + PLOT_W} y2={svgY(n)} className="coord-grid" />
        </g>
      ))}

      {/* Axes */}
      <line x1={ox - 10} y1={oy} x2={xArrowTip} y2={oy} className="coord-axis" />
      <line x1={ox} y1={oy + 10} x2={ox} y2={yArrowTip} className="coord-axis" />

      {/* Arrowheads */}
      <polygon points={`${xArrowTip},${oy} ${xArrowTip-10},${oy-5} ${xArrowTip-10},${oy+5}`} className="coord-arrow" />
      <polygon points={`${ox},${yArrowTip} ${ox-5},${yArrowTip+10} ${ox+5},${yArrowTip+10}`} className="coord-arrow" />

      {/* Axis labels */}
      <text x={xArrowTip+8} y={oy} textAnchor="start" dominantBaseline="middle" className="coord-label coord-label--annotation">x-axis</text>
      <text transform="rotate(-90)" x={-(yArrowTip-8)} y={ox-12} textAnchor="end" className="coord-label coord-label--annotation">y-axis</text>

      {/* Ticks + number labels — highlight the shared x column when vertical */}
      {INTEGERS.map((n) => {
        if (n === 0) return null;
        const isSharedX = showLine && points[0] && n === points[0].x;
        return (
          <g key={n}>
            <line
              x1={svgX(n)} y1={oy - TICK} x2={svgX(n)} y2={oy + TICK}
              className={isSharedX ? 'coord-tick coord-tick--active' : 'coord-tick'}
            />
            <text
              x={svgX(n)} y={oy + LABEL_OFFSET + 4} textAnchor="middle"
              className={isSharedX ? 'coord-label coord-label--active' : 'coord-label'}
            >{n}</text>
            <line x1={ox - TICK} y1={svgY(n)} x2={ox + TICK} y2={svgY(n)} className="coord-tick" />
            <text x={ox - LABEL_OFFSET} y={svgY(n) + 4} textAnchor="end" className="coord-label">{n}</text>
          </g>
        );
      })}

      {/* Origin */}
      <text x={ox - 8} y={oy + LABEL_OFFSET + 4} textAnchor="end" className="coord-label coord-label--zero">0</text>
      <text x={ox + 6} y={oy + LABEL_OFFSET + 4} textAnchor="start" className="coord-label coord-label--annotation">origin</text>

      {/* Green vertical guide line through all points when they share x */}
      {showLine && points.length >= 2 && (
        <line
          key={`vert-${points[0].x}`}
          x1={svgX(points[0].x)} y1={oy - PLOT_H}
          x2={svgX(points[0].x)} y2={oy}
          className="coord-vert-line"
        />
      )}

      {/* Points + coordinate labels */}
      {points.map((p) => {
        const cx          = svgX(p.x);
        const cy          = svgY(p.y);
        const labelAnchor  = p.x >= 8 ? 'end'  : 'start';
        const labelOffsetX = p.x >= 8 ? -10    : 10;
        const labelOffsetY = p.y >= 9 ?  18    : -10;
        return (
          <g key={`${p.label}-${p.x}-${p.y}`}>
            <circle cx={cx} cy={cy} r={6} className="coord-plotted-point" />
            <text
              x={cx + labelOffsetX} y={cy + labelOffsetY}
              textAnchor={labelAnchor}
              className="coord-label coord-label--point-label"
            >{p.label}({p.x}, {p.y})</text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── Measure Distance Mode ───────────────────────────────────────────────── */
const MEASURE_DEFAULTS = { A: { x: 2, y: 4 }, B: { x: 7, y: 4 } };

function MeasureDistanceMode() {
  const [inputs, setInputs] = useState([
    { label: 'A', inputX: String(MEASURE_DEFAULTS.A.x), inputY: String(MEASURE_DEFAULTS.A.y) },
    { label: 'B', inputX: String(MEASURE_DEFAULTS.B.x), inputY: String(MEASURE_DEFAULTS.B.y) },
  ]);
  const [plotted, setPlotted]               = useState(null);
  const [showAnnotation, setShowAnnotation] = useState(false);

  const handleChange = (i, field, val) => {
    setInputs((prev) => prev.map((p, idx) => idx === i ? { ...p, [field]: val } : p));
    setPlotted(null);
    setShowAnnotation(false);
  };

  const parsed = inputs.map((p) => ({
    label : p.label,
    x     : parseInt(p.inputX, 10),
    y     : parseInt(p.inputY, 10),
  }));
  const allValid = parsed.every(
    (p) => !isNaN(p.x) && !isNaN(p.y) && p.x >= 0 && p.x <= MAX && p.y >= 0 && p.y <= MAX
  );
  const isSameY = allValid && parsed[0].y === parsed[1].y;
  const isSameX = allValid && parsed[0].x === parsed[1].x;
  const isLine  = isSameY || isSameX;

  const handleRandom = () => {
    setPlotted(null);
    setShowAnnotation(false);
    if (Math.random() < 0.5) {
      const y  = 1 + Math.floor(Math.random() * 9);
      const x1 = 1 + Math.floor(Math.random() * 5);
      const x2 = Math.min(x1 + 2 + Math.floor(Math.random() * 4), 10);
      setInputs([
        { label: 'A', inputX: String(x1), inputY: String(y) },
        { label: 'B', inputX: String(x2), inputY: String(y) },
      ]);
    } else {
      const x  = 1 + Math.floor(Math.random() * 9);
      const y1 = 1 + Math.floor(Math.random() * 5);
      const y2 = Math.min(y1 + 2 + Math.floor(Math.random() * 4), 10);
      setInputs([
        { label: 'A', inputX: String(x), inputY: String(y1) },
        { label: 'B', inputX: String(x), inputY: String(y2) },
      ]);
    }
  };

  const handlePlot = () => {
    if (!allValid) return;
    setShowAnnotation(false);
    setPlotted(parsed);
    if (isLine) setTimeout(() => setShowAnnotation(true), 900);
  };

  const handleReset = () => {
    setInputs([
      { label: 'A', inputX: String(MEASURE_DEFAULTS.A.x), inputY: String(MEASURE_DEFAULTS.A.y) },
      { label: 'B', inputX: String(MEASURE_DEFAULTS.B.x), inputY: String(MEASURE_DEFAULTS.B.y) },
    ]);
    setPlotted(null);
    setShowAnnotation(false);
  };

  let distanceFormula = null;
  if (plotted && isLine) {
    const A = plotted[0];
    const B = plotted[1];
    if (isSameY) {
      const big   = Math.max(A.x, B.x);
      const small = Math.min(A.x, B.x);
      distanceFormula = { axis: 'x', big, small, dist: big - small };
    } else {
      const big   = Math.max(A.y, B.y);
      const small = Math.min(A.y, B.y);
      distanceFormula = { axis: 'y', big, small, dist: big - small };
    }
  }

  return (
    <div className="coord-lab--plot">
      <div className="coord-plot-canvas-wrap">
        <MeasureCanvas
          points={plotted || []}
          showAnnotation={showAnnotation}
          isSameY={isSameY}
          isSameX={isSameX}
        />
      </div>

      <div className="coord-plot-controls">
        <div className="coord-plot-input-group">
          <div className="coord-horiz-point-row coord-horiz-col-headers">
            <span className="coord-horiz-point-heading" />
            <span className="coord-plot-field-label coord-plot-field-label--x">x</span>
            <span className="coord-plot-field-label coord-plot-field-label--y">y</span>
          </div>
          {inputs.map((p, i) => (
            <div key={p.label} className="coord-horiz-point-row">
              <span className="coord-horiz-point-heading">{p.label}</span>
              <input
                className="coord-plot-input"
                type="number" min="0" max="10"
                value={p.inputX}
                onChange={(e) => handleChange(i, 'inputX', e.target.value)}
                placeholder="0-10"
              />
              <input
                className="coord-plot-input"
                type="number" min="0" max="10"
                value={p.inputY}
                onChange={(e) => handleChange(i, 'inputY', e.target.value)}
                placeholder="0-10"
              />
            </div>
          ))}
          <button type="button" className="coord-plot-dice-btn" onClick={handleRandom}>
            Random
          </button>
        </div>

        <div className="coord-plot-divider" />

        <button
          type="button"
          className="btn btn-secondary coord-plot-reset-btn"
          onClick={handlePlot}
          disabled={!allValid}
        >
          Plot points
        </button>

        {plotted !== null && !isLine && (
          <div className="coord-horiz-feedback is-no">
            Points don't form a straight line
          </div>
        )}

        {distanceFormula && showAnnotation && (
          <div className="coord-measure-formula-panel">
            <div className="coord-measure-formula-title">Distance by subtraction</div>
            <div className="coord-measure-formula-step">
              {distanceFormula.axis === 'x'
                ? `x\u2082 \u2212 x\u2081 = ${distanceFormula.big} \u2212 ${distanceFormula.small}`
                : `y\u2082 \u2212 y\u2081 = ${distanceFormula.big} \u2212 ${distanceFormula.small}`}
            </div>
            <div className="coord-measure-formula-result">
              = {distanceFormula.dist} units
            </div>
          </div>
        )}

        <button type="button" className="coord-plot-dice-btn" onClick={handleReset}>
          Reset
        </button>
      </div>
    </div>
  );
}

/* ── Measure Distance Canvas ─────────────────────────────────────────────── */
function MeasureCanvas({ points, showAnnotation, isSameY, isSameX }) {
  const CELL     = 46;
  const PAD      = 54;
  const PLOT_W   = MAX * CELL;
  const PLOT_H   = MAX * CELL;
  const W        = PAD + PLOT_W + PAD;
  const H        = PAD + PLOT_H + PAD;
  const VB_RIGHT = 72;
  const VB_TOP   = 44;

  const ox = PAD;
  const oy = H - PAD;

  const svgX = (n) => ox + (n / MAX) * PLOT_W;
  const svgY = (n) => oy - (n / MAX) * PLOT_H;

  const TICK         = 5;
  const LABEL_OFFSET = 14;
  const xArrowTip    = ox + PLOT_W + 14;
  const yArrowTip    = oy - PLOT_H - 14;

  const A = points[0];
  const B = points[1];

  let segmentEl    = null;
  let annotationEl = null;

  if (A && B) {
    const ax = svgX(A.x), ay = svgY(A.y);
    const bx = svgX(B.x), by = svgY(B.y);
    const len = Math.sqrt((bx - ax) ** 2 + (by - ay) ** 2);

    segmentEl = (
      <line
        x1={ax} y1={ay} x2={bx} y2={by}
        className="coord-measure-segment"
        style={{ '--line-len': len }}
      />
    );

    if (showAnnotation) {
      const ANNOT_GAP = 22;
      const TICK_H    = 6;

      if (isSameY) {
        const minX  = Math.min(ax, bx);
        const maxX  = Math.max(ax, bx);
        const bktY  = ay - ANNOT_GAP;
        const big   = Math.max(A.x, B.x);
        const small = Math.min(A.x, B.x);
        const midX  = (minX + maxX) / 2;
        annotationEl = (
          <g className="coord-measure-annot">
            <line x1={minX} y1={ay - TICK_H} x2={minX} y2={bktY} className="coord-measure-ext" />
            <line x1={maxX} y1={ay - TICK_H} x2={maxX} y2={bktY} className="coord-measure-ext" />
            <line x1={minX} y1={bktY} x2={maxX} y2={bktY} className="coord-measure-bracket" />
            <line x1={minX} y1={bktY - TICK_H / 2} x2={minX} y2={bktY + TICK_H / 2} className="coord-measure-bracket" />
            <line x1={maxX} y1={bktY - TICK_H / 2} x2={maxX} y2={bktY + TICK_H / 2} className="coord-measure-bracket" />
            <text x={midX} y={bktY - 8} textAnchor="middle" className="coord-measure-text">
              {big} - {small} = {big - small}
            </text>
          </g>
        );
      } else if (isSameX) {
        const minY  = Math.min(ay, by);
        const maxY  = Math.max(ay, by);
        const bktX  = ax + ANNOT_GAP;
        const big   = Math.max(A.y, B.y);
        const small = Math.min(A.y, B.y);
        const midY  = (minY + maxY) / 2;
        annotationEl = (
          <g className="coord-measure-annot">
            <line x1={ax + TICK_H} y1={minY} x2={bktX} y2={minY} className="coord-measure-ext" />
            <line x1={ax + TICK_H} y1={maxY} x2={bktX} y2={maxY} className="coord-measure-ext" />
            <line x1={bktX} y1={minY} x2={bktX} y2={maxY} className="coord-measure-bracket" />
            <line x1={bktX - TICK_H / 2} y1={minY} x2={bktX + TICK_H / 2} y2={minY} className="coord-measure-bracket" />
            <line x1={bktX - TICK_H / 2} y1={maxY} x2={bktX + TICK_H / 2} y2={maxY} className="coord-measure-bracket" />
            <text x={bktX + 10} y={midY + 4} textAnchor="start" className="coord-measure-text">
              {big} - {small} = {big - small}
            </text>
          </g>
        );
      }
    }
  }

  return (
    <svg
      viewBox={`0 ${-VB_TOP} ${W + VB_RIGHT} ${H + VB_TOP}`}
      className="coord-lab-svg--natural coord-lab-svg--measure"
      aria-label="Coordinate plane for measuring distance"
      role="img"
    >
      {/* Grid */}
      {INTEGERS.filter((n) => n > 0).map((n) => (
        <g key={n}>
          <line x1={svgX(n)} y1={oy} x2={svgX(n)} y2={oy - PLOT_H} className="coord-grid" />
          <line x1={ox} y1={svgY(n)} x2={ox + PLOT_W} y2={svgY(n)} className="coord-grid" />
        </g>
      ))}

      {/* Axes */}
      <line x1={ox - 10} y1={oy} x2={xArrowTip} y2={oy} className="coord-axis" />
      <line x1={ox} y1={oy + 10} x2={ox} y2={yArrowTip} className="coord-axis" />

      {/* Arrowheads */}
      <polygon points={`${xArrowTip},${oy} ${xArrowTip-10},${oy-5} ${xArrowTip-10},${oy+5}`} className="coord-arrow" />
      <polygon points={`${ox},${yArrowTip} ${ox-5},${yArrowTip+10} ${ox+5},${yArrowTip+10}`} className="coord-arrow" />

      {/* Axis labels */}
      <text x={xArrowTip+8} y={oy} textAnchor="start" dominantBaseline="middle" className="coord-label coord-label--annotation">x-axis</text>
      <text transform="rotate(-90)" x={-(yArrowTip-8)} y={ox-12} textAnchor="end" className="coord-label coord-label--annotation">y-axis</text>

      {/* Ticks + number labels */}
      {INTEGERS.map((n) => {
        if (n === 0) return null;
        return (
          <g key={n}>
            <line x1={svgX(n)} y1={oy - TICK} x2={svgX(n)} y2={oy + TICK} className="coord-tick" />
            <text x={svgX(n)} y={oy + LABEL_OFFSET + 4} textAnchor="middle" className="coord-label">{n}</text>
            <line x1={ox - TICK} y1={svgY(n)} x2={ox + TICK} y2={svgY(n)} className="coord-tick" />
            <text x={ox - LABEL_OFFSET} y={svgY(n) + 4} textAnchor="end" className="coord-label">{n}</text>
          </g>
        );
      })}

      {/* Origin */}
      <text x={ox - 8} y={oy + LABEL_OFFSET + 4} textAnchor="end" className="coord-label coord-label--zero">0</text>
      <text x={ox + 6} y={oy + LABEL_OFFSET + 4} textAnchor="start" className="coord-label coord-label--annotation">origin</text>

      {/* Connecting segment */}
      {segmentEl}

      {/* Annotation bracket + formula */}
      {annotationEl}

      {/* Points + labels */}
      {points.map((p) => {
        const cx           = svgX(p.x);
        const cy           = svgY(p.y);
        const labelAnchor  = p.x >= 8 ? 'end'  : 'start';
        const labelOffsetX = p.x >= 8 ? -10    : 10;
        const labelOffsetY = p.y >= 9 ?  18    : -10;
        return (
          <g key={`${p.label}-${p.x}-${p.y}`}>
            <circle cx={cx} cy={cy} r={6} className="coord-plotted-point" />
            <text
              x={cx + labelOffsetX} y={cy + labelOffsetY}
              textAnchor={labelAnchor}
              className="coord-label coord-label--point-label"
            >{p.label}({p.x}, {p.y})</text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── Plot Shape Mode ─────────────────────────────────────────────────────── */
const SHAPE_DEFAULTS = [
  { label: 'A', x: 1, y: 1 },
  { label: 'B', x: 5, y: 1 },
  { label: 'C', x: 5, y: 4 },
  { label: 'D', x: 1, y: 4 },
];

// Sides connect in order: A-B, B-C, C-D, D-A
const SHAPE_SIDES = [
  { key: 'AB', from: 0, to: 1 },
  { key: 'BC', from: 1, to: 2 },
  { key: 'CD', from: 2, to: 3 },
  { key: 'DA', from: 3, to: 0 },
];

function PlotShapeMode() {
  const [inputs, setInputs] = useState(
    SHAPE_DEFAULTS.map((p) => ({ label: p.label, inputX: String(p.x), inputY: String(p.y) }))
  );
  const [plotted, setPlotted]   = useState(null);
  const [animStep, setAnimStep] = useState(0); // 0=idle, 1-4=points appear, 5-8=sides draw
  const [plotKey, setPlotKey]   = useState(0); // bumped each plot to re-trigger animations

  const handleChange = (i, field, val) => {
    setInputs((prev) => prev.map((p, idx) => idx === i ? { ...p, [field]: val } : p));
    setAnimStep(0);
    setPlotted(null);
  };

  const parsed = inputs.map((p) => ({
    label : p.label,
    x     : parseInt(p.inputX, 10),
    y     : parseInt(p.inputY, 10),
  }));
  const allValid = parsed.every(
    (p) => !isNaN(p.x) && !isNaN(p.y) && p.x >= 0 && p.x <= MAX && p.y >= 0 && p.y <= MAX
  );

  const handleRandom = () => {
    setAnimStep(0);
    setPlotted(null);
    // Generate a random rectangle
    const x1 = 1 + Math.floor(Math.random() * 4);
    const x2 = Math.min(x1 + 2 + Math.floor(Math.random() * 4), 10);
    const y1 = 1 + Math.floor(Math.random() * 4);
    const y2 = Math.min(y1 + 2 + Math.floor(Math.random() * 4), 10);
    setInputs([
      { label: 'A', inputX: String(x1), inputY: String(y1) },
      { label: 'B', inputX: String(x2), inputY: String(y1) },
      { label: 'C', inputX: String(x2), inputY: String(y2) },
      { label: 'D', inputX: String(x1), inputY: String(y2) },
    ]);
  };

  const handlePlot = () => {
    if (!allValid) return;
    setPlotted(parsed);
    setAnimStep(0);
    setPlotKey((k) => k + 1);
    // Points appear one by one: steps 1-4
    const POINT_MS = 400;
    // Sides draw one by one: steps 5-8 (staggered by 700ms so each starts before prev finishes)
    const SIDE_MS  = 700;
    const SIDE_START = 4 * POINT_MS + 100;
    for (let i = 1; i <= 4; i++) {
      setTimeout(() => setAnimStep(i), i * POINT_MS);
    }
    for (let i = 1; i <= 4; i++) {
      setTimeout(() => setAnimStep(4 + i), SIDE_START + i * SIDE_MS);
    }
  };

  const handleReset = () => {
    setInputs(SHAPE_DEFAULTS.map((p) => ({ label: p.label, inputX: String(p.x), inputY: String(p.y) })));
    setAnimStep(0);
    setPlotted(null);
  };

  return (
    <div className="coord-lab--plot">
      <div className="coord-plot-canvas-wrap">
        <ShapeCanvas points={plotted || []} animStep={animStep} plotKey={plotKey} />
      </div>

      <div className="coord-plot-controls">
        <div className="coord-plot-input-group">
          <div className="coord-horiz-point-row coord-horiz-col-headers">
            <span className="coord-horiz-point-heading" />
            <span className="coord-plot-field-label coord-plot-field-label--x">x</span>
            <span className="coord-plot-field-label coord-plot-field-label--y">y</span>
          </div>
          {inputs.map((p, i) => (
            <div key={p.label} className="coord-horiz-point-row">
              <span className="coord-horiz-point-heading">{p.label}</span>
              <input
                className="coord-plot-input"
                type="number" min="0" max="10"
                value={p.inputX}
                onChange={(e) => handleChange(i, 'inputX', e.target.value)}
                placeholder="0-10"
              />
              <input
                className="coord-plot-input"
                type="number" min="0" max="10"
                value={p.inputY}
                onChange={(e) => handleChange(i, 'inputY', e.target.value)}
                placeholder="0-10"
              />
            </div>
          ))}
          <button type="button" className="coord-plot-dice-btn" onClick={handleRandom}>
            🎲 Random
          </button>
        </div>

        <div className="coord-plot-divider" />

        <button
          type="button"
          className="btn btn-secondary coord-plot-reset-btn"
          onClick={handlePlot}
          disabled={!allValid}
        >
          Plot shape
        </button>

        <button type="button" className="coord-plot-dice-btn" onClick={handleReset}>
          Reset
        </button>
      </div>
    </div>
  );
}

/* ── Shape Canvas ────────────────────────────────────────────────────────── */
function ShapeCanvas({ points, animStep, plotKey }) {
  const CELL     = 46;
  const PAD      = 54;
  const PLOT_W   = MAX * CELL;
  const PLOT_H   = MAX * CELL;
  const W        = PAD + PLOT_W + PAD;
  const H        = PAD + PLOT_H + PAD;
  const VB_RIGHT = 72;
  const VB_TOP   = 44;

  const ox = PAD;
  const oy = H - PAD;

  const svgX = (n) => ox + (n / MAX) * PLOT_W;
  const svgY = (n) => oy - (n / MAX) * PLOT_H;

  const TICK         = 5;
  const LABEL_OFFSET = 14;
  const xArrowTip    = ox + PLOT_W + 14;
  const yArrowTip    = oy - PLOT_H - 14;

  // Centroid for label offset direction
  const centroidX = points.length === 4
    ? points.reduce((s, p) => s + svgX(p.x), 0) / 4
    : 0;
  const centroidY = points.length === 4
    ? points.reduce((s, p) => s + svgY(p.y), 0) / 4
    : 0;

  return (
    <svg
      viewBox={`0 ${-VB_TOP} ${W + VB_RIGHT} ${H + VB_TOP}`}
      className="coord-lab-svg--natural coord-lab-svg--shape"
      aria-label="Coordinate plane with plotted shape"
      role="img"
    >
      {/* Grid */}
      {INTEGERS.filter((n) => n > 0).map((n) => (
        <g key={n}>
          <line x1={svgX(n)} y1={oy} x2={svgX(n)} y2={oy - PLOT_H} className="coord-grid" />
          <line x1={ox} y1={svgY(n)} x2={ox + PLOT_W} y2={svgY(n)} className="coord-grid" />
        </g>
      ))}

      {/* Axes */}
      <line x1={ox - 10} y1={oy} x2={xArrowTip} y2={oy} className="coord-axis" />
      <line x1={ox} y1={oy + 10} x2={ox} y2={yArrowTip} className="coord-axis" />

      {/* Arrowheads */}
      <polygon points={`${xArrowTip},${oy} ${xArrowTip-10},${oy-5} ${xArrowTip-10},${oy+5}`} className="coord-arrow" />
      <polygon points={`${ox},${yArrowTip} ${ox-5},${yArrowTip+10} ${ox+5},${yArrowTip+10}`} className="coord-arrow" />

      {/* Axis labels */}
      <text x={xArrowTip+8} y={oy} textAnchor="start" dominantBaseline="middle" className="coord-label coord-label--annotation">x-axis</text>
      <text transform="rotate(-90)" x={-(yArrowTip-8)} y={ox-12} textAnchor="end" className="coord-label coord-label--annotation">y-axis</text>

      {/* Ticks + number labels */}
      {INTEGERS.map((n) => {
        if (n === 0) return null;
        return (
          <g key={n}>
            <line x1={svgX(n)} y1={oy - TICK} x2={svgX(n)} y2={oy + TICK} className="coord-tick" />
            <text x={svgX(n)} y={oy + LABEL_OFFSET + 4} textAnchor="middle" className="coord-label">{n}</text>
            <line x1={ox - TICK} y1={svgY(n)} x2={ox + TICK} y2={svgY(n)} className="coord-tick" />
            <text x={ox - LABEL_OFFSET} y={svgY(n) + 4} textAnchor="end" className="coord-label">{n}</text>
          </g>
        );
      })}

      {/* Origin */}
      <text x={ox - 8} y={oy + LABEL_OFFSET + 4} textAnchor="end" className="coord-label coord-label--zero">0</text>
      <text x={ox + 6} y={oy + LABEL_OFFSET + 4} textAnchor="start" className="coord-label coord-label--annotation">origin</text>

      {/* Sides — draw one by one as animStep reaches 5, 6, 7, 8 */}
      {points.length === 4 && SHAPE_SIDES.map((side, i) => {
        if (animStep < 5 + i) return null;
        const A = points[side.from];
        const B = points[side.to];
        const ax = svgX(A.x), ay = svgY(A.y);
        const bx = svgX(B.x), by = svgY(B.y);
        const len = Math.sqrt((bx - ax) ** 2 + (by - ay) ** 2);
        return (
          <line
            key={`${plotKey}-${side.key}`}
            x1={ax} y1={ay} x2={bx} y2={by}
            className="coord-shape-side"
            style={{ '--line-len': len }}
          />
        );
      })}

      {/* Points — reveal one by one as animStep reaches 1, 2, 3, 4 */}
      {points.slice(0, Math.min(animStep, 4)).map((p) => {
        const px = svgX(p.x);
        const py = svgY(p.y);
        // Push label away from centroid
        const dx = px - centroidX;
        const dy = py - centroidY;
        const angle = Math.atan2(dy, dx);
        const labelX = px + Math.cos(angle) * 16;
        const labelY = py + Math.sin(angle) * 16;
        const anchor = dx >= 0 ? 'start' : 'end';
        return (
          <g key={`${plotKey}-pt-${p.label}`}>
            <circle cx={px} cy={py} r={6} className="coord-plotted-point" />
            <text
              x={labelX} y={labelY + 4}
              textAnchor={anchor}
              className="coord-label coord-label--point-label"
            >{p.label}({p.x},{p.y})</text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── Plot Graph Mode ─────────────────────────────────────────────────────── */
const GRAPH_DEFAULTS = [
  { month: 1, value: 12 },
  { month: 2, value: 18 },
  { month: 3, value: 14 },
  { month: 4, value: 8 },
  { month: 5, value: 3 },
  { month: 6, value: 0 },
];

const GRAPH_X_MAX  = 7;   // grid units — months 1-6 plotted within 0-7
const GRAPH_Y_MAX  = 8;   // grid units — each = 5 cm → 0-40 cm
const GRAPH_Y_STEP = 5;   // cm per grid unit  (scale 1:5)

function PlotGraphMode() {
  const [inputs, setInputs] = useState(
    GRAPH_DEFAULTS.map((d) => ({ month: d.month, inputVal: String(d.value) }))
  );
  const [plotted, setPlotted]   = useState(null);
  const [animStep, setAnimStep] = useState(0);
  const [plotKey, setPlotKey]   = useState(0);

  const handleChange = (i, val) => {
    setInputs((prev) => prev.map((p, idx) => idx === i ? { ...p, inputVal: val } : p));
    setAnimStep(0);
    setPlotted(null);
  };

  const parsed = inputs.map((p) => ({
    month: p.month,
    value: parseInt(p.inputVal, 10),
  }));
  const allValid = parsed.every(
    (p) => !isNaN(p.value) && p.value >= 0 && p.value <= 40
  );

  const handleRandom = () => {
    setAnimStep(0);
    setPlotted(null);
    setInputs(GRAPH_DEFAULTS.map((d) => ({
      month: d.month,
      inputVal: String(Math.floor(Math.random() * 26)), // 0-25 cm
    })));
  };

  const handlePlot = () => {
    if (!allValid) return;
    setPlotted(parsed);
    setAnimStep(0);
    setPlotKey((k) => k + 1);
    for (let i = 1; i <= parsed.length; i++) {
      setTimeout(() => setAnimStep(i), i * 600);
    }
  };

  const handleReset = () => {
    setInputs(GRAPH_DEFAULTS.map((d) => ({ month: d.month, inputVal: String(d.value) })));
    setAnimStep(0);
    setPlotted(null);
  };

  return (
    <div className="coord-lab--plot">
      <div className="coord-plot-canvas-wrap">
        <GraphCanvas points={plotted || []} animStep={animStep} plotKey={plotKey} />
      </div>

      <div className="coord-plot-controls">
        <div className="coord-plot-input-group">
          <div className="coord-graph-row coord-graph-col-header">
            <span className="coord-graph-mo-label">Mo.</span>
            <span className="coord-plot-field-label coord-graph-val-header">cm</span>
          </div>
          {inputs.map((p, i) => (
            <div key={p.month} className="coord-graph-row">
              <span className="coord-graph-mo-label">{p.month}</span>
              <input
                className="coord-plot-input"
                type="number" min="0" max="40"
                value={p.inputVal}
                onChange={(e) => handleChange(i, e.target.value)}
                placeholder="0–40"
              />
            </div>
          ))}
          <button type="button" className="coord-plot-dice-btn" onClick={handleRandom}>
            🎲 Random
          </button>
        </div>

        <div className="coord-plot-divider" />

        <button
          type="button"
          className="btn btn-secondary coord-plot-reset-btn"
          onClick={handlePlot}
          disabled={!allValid}
        >
          Plot data
        </button>

        <button type="button" className="coord-plot-dice-btn" onClick={handleReset}>
          Reset
        </button>
      </div>
    </div>
  );
}

/* ── Graph Canvas ────────────────────────────────────────────────────────── */
function GraphCanvas({ points, animStep, plotKey }) {
  const CELL     = 46;
  const PAD      = 64;
  const PLOT_W   = GRAPH_X_MAX * CELL;   // 7 * 46 = 322
  const PLOT_H   = GRAPH_Y_MAX * CELL;   // 8 * 46 = 368
  const W        = PAD + PLOT_W + PAD;   // 450
  const H        = PAD + PLOT_H + PAD;   // 496
  const VB_RIGHT = 110;
  const VB_TOP   = 54;

  const ox = PAD;         // 64
  const oy = H - PAD;     // 432

  const svgX = (month) => ox + (month / GRAPH_X_MAX) * PLOT_W;
  const svgY = (cm)    => oy - (cm / (GRAPH_Y_MAX * GRAPH_Y_STEP)) * PLOT_H;
  // GRAPH_Y_MAX * GRAPH_Y_STEP = 8 * 5 = 40 cm total

  const TICK         = 5;
  const LABEL_OFFSET = 18;
  const xArrowTip    = ox + PLOT_W + 14;
  const yArrowTip    = oy - PLOT_H - 14;

  const Y_TICKS = Array.from({ length: GRAPH_Y_MAX + 1 }, (_, i) => i * GRAPH_Y_STEP); // 0,5,10..40
  const X_TICKS = [1, 2, 3, 4, 5, 6];

  return (
    <svg
      viewBox={`0 ${-VB_TOP} ${W + VB_RIGHT} ${H + VB_TOP}`}
      className="coord-lab-svg--natural coord-lab-svg--graph"
      aria-label="Snowfall graph on coordinate plane"
      role="img"
    >
      {/* Grid — vertical at each month */}
      {X_TICKS.map((m) => (
        <line key={`vg-${m}`} x1={svgX(m)} y1={oy} x2={svgX(m)} y2={oy - PLOT_H} className="coord-grid" />
      ))}
      {/* Grid — horizontal at each 5 cm step */}
      {Y_TICKS.filter((v) => v > 0).map((v) => (
        <line key={`hg-${v}`} x1={ox} y1={svgY(v)} x2={ox + PLOT_W} y2={svgY(v)} className="coord-grid" />
      ))}

      {/* Axes */}
      <line x1={ox - 10} y1={oy} x2={xArrowTip} y2={oy} className="coord-axis" />
      <line x1={ox} y1={oy + 10} x2={ox} y2={yArrowTip} className="coord-axis" />

      {/* Arrowheads */}
      <polygon points={`${xArrowTip},${oy} ${xArrowTip-10},${oy-5} ${xArrowTip-10},${oy+5}`} className="coord-arrow" />
      <polygon points={`${ox},${yArrowTip} ${ox-5},${yArrowTip+10} ${ox+5},${yArrowTip+10}`} className="coord-arrow" />

      {/* Axis title annotations */}
      <text x={xArrowTip+8} y={oy} textAnchor="start" dominantBaseline="middle" className="coord-label coord-label--annotation">
        Month (1–6)
      </text>
      <text
        transform="rotate(-90)"
        x={-(yArrowTip - 8)}
        y={ox - 14}
        textAnchor="end"
        className="coord-label coord-label--annotation"
      >
        Snowfall (cm)
      </text>

      {/* Scale note */}
      <text
        x={ox} y={oy + LABEL_OFFSET + 20}
        textAnchor="start"
        className="coord-graph-scale-note"
      >
        Scale: 1 unit = {GRAPH_Y_STEP} cm  |  y-axis: 0–40 cm
      </text>

      {/* X-axis ticks + month number labels */}
      {X_TICKS.map((m) => (
        <g key={`xt-${m}`}>
          <line x1={svgX(m)} y1={oy - TICK} x2={svgX(m)} y2={oy + TICK} className="coord-tick" />
          <text x={svgX(m)} y={oy + LABEL_OFFSET + 2} textAnchor="middle" className="coord-label">{m}</text>
        </g>
      ))}

      {/* Y-axis ticks + cm value labels */}
      {Y_TICKS.map((v) => (
        <g key={`yt-${v}`}>
          <line x1={ox - TICK} y1={svgY(v)} x2={ox + TICK} y2={svgY(v)} className="coord-tick" />
          <text x={ox - LABEL_OFFSET + 4} y={svgY(v) + 4} textAnchor="end" className="coord-label">{v}</text>
        </g>
      ))}

      {/* Data points + value labels — revealed one by one */}
      {(() => {
        const visible = points.slice(0, animStep);
        const allDone = visible.length === points.length && points.length > 0;
        const maxVal  = allDone ? Math.max(...points.map((p) => p.value)) : null;
        const minVal  = allDone ? Math.min(...points.map((p) => p.value)) : null;
        return visible.map((p) => {
          const px = svgX(p.month);
          const py = svgY(p.value);
          const isHigh = allDone && p.value === maxVal;
          const isLow  = allDone && p.value === minVal && p.value !== maxVal;
          const ptClass = isHigh
            ? 'coord-graph-point coord-graph-point--high'
            : isLow
              ? 'coord-graph-point coord-graph-point--low'
              : 'coord-graph-point';
          return (
            <g key={`${plotKey}-pt-${p.month}`}>
              <circle cx={px} cy={py} r={isHigh || isLow ? 8 : 6} className={ptClass} />
              <text
                x={px} y={py - 14}
                textAnchor="middle"
                className={`coord-graph-point-label${isHigh ? ' coord-graph-point-label--high' : isLow ? ' coord-graph-point-label--low' : ''}`}
              >{p.value} cm{isHigh ? ' ▲' : isLow ? ' ▼' : ''}</text>
            </g>
          );
        });
      })()}
    </svg>
  );
}

/* ── Plot Graph Lines Mode ───────────────────────────────────────────────── */
// Clone of PlotGraphMode but with colored connecting segments:
//   green = rising, red = falling, blue = flat
function PlotGraphLinesMode() {
  const [inputs, setInputs] = useState(
    GRAPH_DEFAULTS.map((d) => ({ month: d.month, inputVal: String(d.value) }))
  );
  const [plotted, setPlotted]   = useState(null);
  const [animStep, setAnimStep] = useState(0);
  const [plotKey, setPlotKey]   = useState(0);

  const handleChange = (i, val) => {
    setInputs((prev) => prev.map((p, idx) => idx === i ? { ...p, inputVal: val } : p));
    setAnimStep(0);
    setPlotted(null);
  };

  const parsed = inputs.map((p) => ({
    month: p.month,
    value: parseInt(p.inputVal, 10),
  }));
  const allValid = parsed.every(
    (p) => !isNaN(p.value) && p.value >= 0 && p.value <= 40
  );

  const handleRandom = () => {
    setAnimStep(0);
    setPlotted(null);
    setInputs(GRAPH_DEFAULTS.map((d) => ({
      month: d.month,
      inputVal: String(Math.floor(Math.random() * 26)),
    })));
  };

  const handlePlot = () => {
    if (!allValid) return;
    setPlotted(parsed);
    setAnimStep(0);
    setPlotKey((k) => k + 1);
    // Each step reveals one point + the segment leading into it
    for (let i = 1; i <= parsed.length; i++) {
      setTimeout(() => setAnimStep(i), i * 600);
    }
  };

  const handleReset = () => {
    setInputs(GRAPH_DEFAULTS.map((d) => ({ month: d.month, inputVal: String(d.value) })));
    setAnimStep(0);
    setPlotted(null);
  };

  return (
    <div className="coord-lab--plot">
      <div className="coord-plot-canvas-wrap">
        <GraphLinesCanvas points={plotted || []} animStep={animStep} plotKey={plotKey} />
      </div>

      <div className="coord-plot-controls">
        <div className="coord-plot-input-group">
          <div className="coord-graph-row coord-graph-col-header">
            <span className="coord-graph-mo-label">Mo.</span>
            <span className="coord-plot-field-label coord-graph-val-header">cm</span>
          </div>
          {inputs.map((p, i) => (
            <div key={p.month} className="coord-graph-row">
              <span className="coord-graph-mo-label">{p.month}</span>
              <input
                className="coord-plot-input"
                type="number" min="0" max="40"
                value={p.inputVal}
                onChange={(e) => handleChange(i, e.target.value)}
                placeholder="0–40"
              />
            </div>
          ))}
          <button type="button" className="coord-plot-dice-btn" onClick={handleRandom}>
            🎲 Random
          </button>
        </div>

        <div className="coord-plot-divider" />

        <button
          type="button"
          className="btn btn-secondary coord-plot-reset-btn"
          onClick={handlePlot}
          disabled={!allValid}
        >
          Plot data
        </button>

        <button type="button" className="coord-plot-dice-btn" onClick={handleReset}>
          Reset
        </button>
      </div>
    </div>
  );
}

/* ── Graph Lines Canvas ──────────────────────────────────────────────────── */
function GraphLinesCanvas({ points, animStep, plotKey }) {
  const CELL     = 46;
  const PAD      = 64;
  const PLOT_W   = GRAPH_X_MAX * CELL;
  const PLOT_H   = GRAPH_Y_MAX * CELL;
  const W        = PAD + PLOT_W + PAD;
  const H        = PAD + PLOT_H + PAD;
  const VB_RIGHT = 110;
  const VB_TOP   = 54;

  const ox = PAD;
  const oy = H - PAD;

  const svgX = (month) => ox + (month / GRAPH_X_MAX) * PLOT_W;
  const svgY = (cm)    => oy - (cm / (GRAPH_Y_MAX * GRAPH_Y_STEP)) * PLOT_H;

  const TICK         = 5;
  const LABEL_OFFSET = 18;
  const xArrowTip    = ox + PLOT_W + 14;
  const yArrowTip    = oy - PLOT_H - 14;

  const Y_TICKS = Array.from({ length: GRAPH_Y_MAX + 1 }, (_, i) => i * GRAPH_Y_STEP);
  const X_TICKS = [1, 2, 3, 4, 5, 6];

  const visiblePoints = points.slice(0, animStep);
  const allDone       = visiblePoints.length === points.length && points.length > 0;
  const maxVal = allDone ? Math.max(...points.map((p) => p.value)) : null;
  const minVal = allDone ? Math.min(...points.map((p) => p.value)) : null;

  const segmentColor = (a, b) => {
    if (b.value > a.value) return '#22c55e'; // rising
    if (b.value < a.value) return '#f87171'; // falling
    return '#60a5fa';                         // flat
  };

  return (
    <svg
      viewBox={`0 ${-VB_TOP} ${W + VB_RIGHT} ${H + VB_TOP}`}
      className="coord-lab-svg--natural coord-lab-svg--graph-lines"
      aria-label="Snowfall line graph"
      role="img"
    >
      {/* Grid */}
      {X_TICKS.map((m) => (
        <line key={`vg-${m}`} x1={svgX(m)} y1={oy} x2={svgX(m)} y2={oy - PLOT_H} className="coord-grid" />
      ))}
      {Y_TICKS.filter((v) => v > 0).map((v) => (
        <line key={`hg-${v}`} x1={ox} y1={svgY(v)} x2={ox + PLOT_W} y2={svgY(v)} className="coord-grid" />
      ))}

      {/* Axes */}
      <line x1={ox - 10} y1={oy} x2={xArrowTip} y2={oy} className="coord-axis" />
      <line x1={ox} y1={oy + 10} x2={ox} y2={yArrowTip} className="coord-axis" />

      {/* Arrowheads */}
      <polygon points={`${xArrowTip},${oy} ${xArrowTip-10},${oy-5} ${xArrowTip-10},${oy+5}`} className="coord-arrow" />
      <polygon points={`${ox},${yArrowTip} ${ox-5},${yArrowTip+10} ${ox+5},${yArrowTip+10}`} className="coord-arrow" />

      {/* Axis annotations */}
      <text x={xArrowTip+8} y={oy} textAnchor="start" dominantBaseline="middle" className="coord-label coord-label--annotation">
        Month (1–6)
      </text>
      <text
        transform="rotate(-90)"
        x={-(yArrowTip - 8)}
        y={ox - 14}
        textAnchor="end"
        className="coord-label coord-label--annotation"
      >
        Snowfall (cm)
      </text>

      {/* Scale note */}
      <text x={ox} y={oy + LABEL_OFFSET + 20} textAnchor="start" className="coord-graph-scale-note">
        Scale: 1 unit = {GRAPH_Y_STEP} cm  |  y-axis: 0–40 cm
      </text>

      {/* Legend */}
      <g transform={`translate(${ox + PLOT_W - 10}, ${-VB_TOP + 6})`}>
        <circle cx={0}  cy={0} r={4} fill="#22c55e" />
        <text x={8}  y={4} className="coord-graph-legend-text">Rising</text>
        <circle cx={60} cy={0} r={4} fill="#f87171" />
        <text x={68} y={4} className="coord-graph-legend-text">Falling</text>
        <circle cx={124} cy={0} r={4} fill="#60a5fa" />
        <text x={132} y={4} className="coord-graph-legend-text">Flat</text>
      </g>

      {/* X-axis ticks */}
      {X_TICKS.map((m) => (
        <g key={`xt-${m}`}>
          <line x1={svgX(m)} y1={oy - TICK} x2={svgX(m)} y2={oy + TICK} className="coord-tick" />
          <text x={svgX(m)} y={oy + LABEL_OFFSET + 2} textAnchor="middle" className="coord-label">{m}</text>
        </g>
      ))}

      {/* Y-axis ticks */}
      {Y_TICKS.map((v) => (
        <g key={`yt-${v}`}>
          <line x1={ox - TICK} y1={svgY(v)} x2={ox + TICK} y2={svgY(v)} className="coord-tick" />
          <text x={ox - LABEL_OFFSET + 4} y={svgY(v) + 4} textAnchor="end" className="coord-label">{v}</text>
        </g>
      ))}

      {/* Colored segments — each drawn as its destination point is revealed */}
      {visiblePoints.slice(1).map((p, i) => {
        const prev  = visiblePoints[i];
        const color = segmentColor(prev, p);
        const x1 = svgX(prev.month), y1 = svgY(prev.value);
        const x2 = svgX(p.month),    y2 = svgY(p.value);
        const len = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
        return (
          <line
            key={`${plotKey}-seg-${p.month}`}
            x1={x1} y1={y1} x2={x2} y2={y2}
            stroke={color}
            strokeWidth={3}
            strokeLinecap="round"
            strokeDasharray={len}
            strokeDashoffset={len}
            className="coord-graph-line-segment"
          />
        );
      })}

      {/* Points */}
      {visiblePoints.map((p) => {
        const px     = svgX(p.month);
        const py     = svgY(p.value);
        const isHigh = allDone && p.value === maxVal;
        const isLow  = allDone && p.value === minVal && p.value !== maxVal;
        const fill   = isHigh ? '#22c55e' : isLow ? '#f87171' : '#fff';
        const stroke = isHigh ? '#22c55e' : isLow ? '#f87171' : '#0891b2';
        return (
          <g key={`${plotKey}-pt-${p.month}`}>
            <circle
              cx={px} cy={py}
              r={isHigh || isLow ? 8 : 6}
              fill={fill}
              stroke={stroke}
              strokeWidth={2.5}
              className="coord-graph-line-point"
            />
            <text
              x={px} y={py - 14}
              textAnchor="middle"
              className={`coord-graph-point-label${isHigh ? ' coord-graph-point-label--high' : isLow ? ' coord-graph-point-label--low' : ''}`}
            >{p.value} cm{isHigh ? ' ▲' : isLow ? ' ▼' : ''}</text>
          </g>
        );
      })}
    </svg>
  );
}
